import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { CheckCircleOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useNavigate, useParams } from "react-router-dom";

const Container = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const ConfirmationWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 60px 20px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const SuccessBox = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const SuccessIcon = styled(CheckCircleOutlined)`
  font-size: 80px;
  color: #000;
  margin-bottom: 20px;
  display: block;

  @media (max-width: 768px) {
    font-size: 60px;
  }
`;

const SuccessTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #000;
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SuccessMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const OrderNumber = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 40px;
  text-align: center;

  .label {
    font-size: 12px;
    color: #999;
    text-transform: uppercase;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .number {
    font-size: 28px;
    font-weight: 700;
    color: #000;
    font-family: "Courier New", monospace;
  }

  @media (max-width: 768px) {
    padding: 16px;

    .number {
      font-size: 20px;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Section = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #eee;

  h3 {
    font-size: 16px;
    font-weight: 700;
    color: #000;
    margin: 0 0 16px 0;
    text-transform: uppercase;
  }
`;

const AddressInfo = styled.div`
  font-size: 14px;
  line-height: 1.8;
  color: #666;

  p {
    margin: 0 0 8px 0;

    strong {
      color: #000;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const DeliveryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;

    .label {
      font-size: 13px;
      color: #999;
      font-weight: 500;
    }

    .value {
      font-size: 14px;
      color: #000;
      font-weight: 600;
      text-align: right;
      flex: 1;
    }
  }
`;

const OrderItems = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #eee;
  margin-bottom: 40px;

  h3 {
    font-size: 16px;
    font-weight: 700;
    color: #000;
    margin: 0 0 16px 0;
    text-transform: uppercase;
  }
`;

const ItemRow = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 6px;
  margin-bottom: 12px;
  align-items: center;

  &:last-child {
    margin-bottom: 0;
  }

  img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
  }

  .details {
    flex: 1;

    .name {
      font-size: 14px;
      font-weight: 600;
      color: #000;
      margin: 0 0 6px 0;
    }

    .meta {
      font-size: 12px;
      color: #999;
      margin: 0;
    }
  }

  .quantity {
    text-align: center;
    min-width: 50px;

    .qty-label {
      font-size: 11px;
      color: #999;
      text-transform: uppercase;
      margin-bottom: 4px;
    }

    .qty-value {
      font-size: 18px;
      font-weight: 700;
      color: #000;
    }
  }

  .price {
    min-width: 80px;
    text-align: right;

    .price-value {
      font-size: 16px;
      font-weight: 700;
      color: #000;
    }
  }
`;

const OrderSummary = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #eee;
  margin-bottom: 40px;

  h3 {
    font-size: 16px;
    font-weight: 700;
    color: #000;
    margin: 0 0 16px 0;
    text-transform: uppercase;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }

  &.total {
    border-top: 2px solid #000;
    border-bottom: 2px solid #000;
    padding: 16px 0;
    font-size: 16px;
    font-weight: 700;
    margin-top: 12px;
  }

  .label {
    color: #666;
  }

  .value {
    color: #000;
    font-weight: 600;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  padding: 14px 32px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SecondaryButton = styled.button`
  padding: 14px 32px;
  background: #fff;
  color: #000;
  border: 1px solid #000;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #f9f9f9;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TimelineSection = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #eee;
  margin-bottom: 40px;

  h3 {
    font-size: 16px;
    font-weight: 700;
    color: #000;
    margin: 0 0 20px 0;
    text-transform: uppercase;
  }
`;

const Timeline = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 20px;
    left: 0;
    right: 0;
    height: 2px;
    background: #ddd;
    z-index: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;

    &::before {
      display: none;
    }
  }
`;

const TimelineItem = styled.div`
  flex: 1;
  text-align: center;
  position: relative;
  z-index: 1;

  .dot {
    width: 40px;
    height: 40px;
    background: #fff;
    border: 3px solid ${(props) => (props.active ? "#000" : "#ddd")};
    border-radius: 50%;
    margin: 0 auto 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: ${(props) => (props.active ? "#000" : "#ddd")};
  }

  .label {
    font-size: 12px;
    font-weight: 600;
    color: #000;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .date {
    font-size: 11px;
    color: #999;
  }

  @media (max-width: 768px) {
    text-align: left;
    display: flex;
    gap: 12px;
    align-items: flex-start;

    .dot {
      margin: 0;
      flex-shrink: 0;
    }

    .info {
      flex: 1;
    }
  }
`;

const InfoBox = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #eee;
  margin-bottom: 40px;

  .info-title {
    font-size: 14px;
    font-weight: 700;
    color: #000;
    margin: 0 0 12px 0;
  }

  .info-text {
    font-size: 13px;
    color: #666;
    line-height: 1.6;
    margin: 0;
  }
`;

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const token = useSelector((state) => state.token);
  const [orderData, setOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setIsLoading(true);
        const apiBase = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${apiBase}/orders/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data.status && data.data) {
          // Transform API response to match component format
          const apiOrder = data.data;
          const transformedOrder = {
            orderNumber: apiOrder.order_number,
            date: new Date(apiOrder.created_at).toLocaleDateString("en-IN", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }),
            estimatedDelivery: new Date(
              Date.now() + 3 * 24 * 60 * 60 * 1000,
            ).toLocaleDateString("en-IN", {
              year: "numeric",
              month: "long",
              day: "numeric",
            }),
            items: apiOrder.items.map((item) => ({
              id: item.id,
              name: item.product?.name || "Product",
              price: parseFloat(item.price) || 0,
              quantity: item.quantity,
              image: item.product?.images?.[0]?.url || "",
              size: item.variant?.size?.name,
              color: item.variant?.color?.name,
            })),
            address: {
              name: apiOrder.address?.name || "",
              street: apiOrder.address?.address || "",
              city: apiOrder.address?.city || "",
              state: apiOrder.address?.state || "",
              pincode: apiOrder.address?.pincode || "",
              phone: apiOrder.address?.phone || "",
            },
            paymentMethod: apiOrder.payment?.payment_method || "Payment",
            shippingMethod: "Standard Delivery",
            subtotal: parseFloat(apiOrder.subtotal) || 0,
            shipping: parseFloat(apiOrder.shipping) || 0,
            discount: parseFloat(apiOrder.discount) || 0,
            total: parseFloat(apiOrder.total) || 0,
          };
          setOrderData(transformedOrder);
          setError(null);
        } else {
          setError(data.message || "Failed to fetch order details");
          setOrderData(null);
        }
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError("Error fetching order details. Please try again.");
        setOrderData(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (id && token) {
      fetchOrderDetails();
    }

    window.scrollTo(0, 0);
  }, [id, token]);

  // Fallback values if data is not loaded
  const defaultOrderData = {
    orderNumber: "Loading...",
    date: new Date().toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    estimatedDelivery: new Date(
      Date.now() + 3 * 24 * 60 * 60 * 1000,
    ).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    items: [],
    address: {
      name: "",
      street: "",
      city: "",
      state: "",
      pincode: "",
      phone: "",
    },
    paymentMethod: "Payment",
    shippingMethod: "Standard Delivery",
    subtotal: 0,
    shipping: 0,
    discount: 0,
    total: 0,
  };

  const displayOrder = orderData || defaultOrderData;
  const subtotal = displayOrder.subtotal || 0;
  const shipping = displayOrder.shipping || 0;
  const discount = displayOrder.discount || 0;
  const total = displayOrder.total || 0;

  if (error) {
    return (
      <Container>
        <ConfirmationWrapper>
          <SuccessBox>
            <SuccessTitle style={{ color: "#d32f2f" }}>Error</SuccessTitle>
            <SuccessMessage>{error}</SuccessMessage>
            <PrimaryButton onClick={() => navigate("/")}>
              Go to Home
            </PrimaryButton>
          </SuccessBox>
        </ConfirmationWrapper>
        <Footer />
      </Container>
    );
  }

  return (
    <Container>
      {/* <Navbar /> */}

      <ConfirmationWrapper>
        {/* Success Message */}
        <SuccessBox>
          <SuccessIcon />
          <SuccessTitle>Order Confirmed!</SuccessTitle>
          <SuccessMessage>
            Thank you for your purchase. Your order has been successfully placed
            and is being prepared for shipment.
          </SuccessMessage>
        </SuccessBox>

        {/* Order Number */}
        <OrderNumber>
          <div className="label">Order Number</div>
          <div className="number">{displayOrder.orderNumber}</div>
        </OrderNumber>

        {/* Delivery Timeline */}
        <TimelineSection>
          <h3>Order Timeline</h3>
          <Timeline>
            <TimelineItem active>
              <div className="dot">1</div>
              <div className="info">
                <div className="label">Order Placed</div>
                <div className="date">{displayOrder.date}</div>
              </div>
            </TimelineItem>
            <TimelineItem>
              <div className="dot">2</div>
              <div className="info">
                <div className="label">Processing</div>
                <div className="date">1-2 days</div>
              </div>
            </TimelineItem>
            <TimelineItem>
              <div className="dot">3</div>
              <div className="info">
                <div className="label">Shipped</div>
                <div className="date">2-3 days</div>
              </div>
            </TimelineItem>
            <TimelineItem>
              <div className="dot">4</div>
              <div className="info">
                <div className="label">Delivered</div>
                <div className="date">{displayOrder.estimatedDelivery}</div>
              </div>
            </TimelineItem>
          </Timeline>
        </TimelineSection>

        {/* Order Items */}
        <OrderItems>
          <h3>Order Items</h3>
          {displayOrder.items.map((item) => (
            <ItemRow key={item.id}>
              <img src={item.image} alt={item.name} />
              <div className="details">
                <p className="name">{item.name}</p>
                <p className="meta">
                  Size: {item.size} | Color: {item.color}
                </p>
              </div>
              <div className="quantity">
                <div className="qty-label">Qty</div>
                <div className="qty-value">{item.quantity}</div>
              </div>
              <div className="price">
                <div className="price-value">
                  ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                </div>
              </div>
            </ItemRow>
          ))}
        </OrderItems>

        {/* Order Summary and Address Grid */}
        <ContentGrid>
          {/* Order Summary */}
          <Section>
            <h3>Order Summary</h3>
            <SummaryRow>
              <span className="label">Subtotal</span>
              <span className="value">₹{subtotal.toLocaleString("en-IN")}</span>
            </SummaryRow>
            <SummaryRow>
              <span className="label">Shipping</span>
              <span className="value">₹{shipping.toLocaleString("en-IN")}</span>
            </SummaryRow>
            <SummaryRow>
              <span className="label">Discount</span>
              <span className="value">₹{discount.toLocaleString("en-IN")}</span>
            </SummaryRow>
            <SummaryRow className="total">
              <span className="label">Total</span>
              <span className="value">₹{total.toLocaleString("en-IN")}</span>
            </SummaryRow>
          </Section>

          {/* Delivery Address */}
          <Section>
            <h3>Delivery Address</h3>
            <AddressInfo>
              <p>
                <strong>{displayOrder.address.name}</strong>
              </p>
              <p>{displayOrder.address.street}</p>
              <p>
                {displayOrder.address.city}, {displayOrder.address.state}
              </p>
              <p>{displayOrder.address.pincode}</p>
              <p>{displayOrder.address.phone}</p>
            </AddressInfo>
          </Section>
        </ContentGrid>

        {/* Shipping and Payment Info */}
        <ContentGrid>
          <Section>
            <h3>Shipping Method</h3>
            <DeliveryInfo>
              <div className="info-item">
                <span className="label">Method</span>
                <span className="value">{displayOrder.shippingMethod}</span>
              </div>
              <div className="info-item">
                <span className="label">Estimated Delivery</span>
                <span className="value">{displayOrder.estimatedDelivery}</span>
              </div>
              <div className="info-item">
                <span className="label">Tracking</span>
                <span className="value">Coming Soon</span>
              </div>
            </DeliveryInfo>
          </Section>

          <Section>
            <h3>Payment Information</h3>
            <DeliveryInfo>
              <div className="info-item">
                <span className="label">Payment Method</span>
                <span className="value">{displayOrder.paymentMethod}</span>
              </div>
              <div className="info-item">
                <span className="label">Status</span>
                <span className="value">Successful</span>
              </div>
              <div className="info-item">
                <span className="label">Order ID</span>
                <span className="value">{displayOrder.orderNumber}</span>
              </div>
            </DeliveryInfo>
          </Section>
        </ContentGrid>

        {/* Info Box */}
        <InfoBox>
          <p className="info-title">📧 Confirmation Email</p>
          <p className="info-text">
            A detailed confirmation email with order details and tracking
            information has been sent to your registered email address. You can
            also track your order anytime from your account.
          </p>
        </InfoBox>

        {/* Action Buttons */}
        <ButtonGroup>
          <PrimaryButton onClick={() => navigate("/")}>
            <span>Continue Shopping</span>
            <ArrowRightOutlined />
          </PrimaryButton>
          <SecondaryButton onClick={() => navigate("/")}>
            View My Account
          </SecondaryButton>
        </ButtonGroup>
      </ConfirmationWrapper>

      <Footer />
    </Container>
  );
};

export default OrderConfirmation;
